import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  useApi,
  identityApiRef,
  microsoftAuthApiRef,
} from '@backstage/frontend-plugin-api';
import {
  TdsHeader,
  TdsHeaderBrandSymbol,
  TdsHeaderItem,
  TdsIcon,
} from '@scania/tegel-react';
import { appRoutePaths } from './navigation';

type UserProfile = {
  displayName?: string;
  email?: string;
  picture?: string;
  photo?: string;
  avatarUrl?: string;
  image?: string;
  profilePicture?: string;
};

const getInitials = (profile?: UserProfile | null) =>
  (profile?.displayName || profile?.email?.split('@')[0] || 'User')
    .split(' ')
    .filter(Boolean)
    .map(name => name[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

const profileAvatar = (profile?: UserProfile | null) =>
  profile?.picture ||
  profile?.photo ||
  profile?.avatarUrl ||
  profile?.image ||
  profile?.profilePicture;

const HeaderUserSettings = () => {
  const identityApi = useApi(identityApiRef);
  const microsoftAuthApi = useApi(microsoftAuthApiRef);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [microsoftPhoto, setMicrosoftPhoto] = useState<string>();

  useEffect(() => {
    let mounted = true;
    let photoObjectUrl: string | undefined;

    const loadProfile = async () => {
      try {
        const [identityProfile, microsoftProfile] = await Promise.all([
          identityApi.getProfileInfo().catch(() => undefined),
          microsoftAuthApi
            .getProfile({ optional: true })
            .catch(() => undefined),
        ]);

        const nextProfile = {
          ...identityProfile,
          ...microsoftProfile,
        };

        if (mounted) {
          setProfile(nextProfile);
          setMicrosoftPhoto(undefined);
        }

        if (profileAvatar(nextProfile)) {
          return;
        }

        const token = await microsoftAuthApi
          .getAccessToken(['User.Read'], { optional: true })
          .catch(() => undefined);

        if (!token) {
          return;
        }

        const response = await fetch(
          'https://graph.microsoft.com/v1.0/me/photo/$value',
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        ).catch(() => undefined);

        if (!response?.ok) {
          return;
        }

        photoObjectUrl = URL.createObjectURL(await response.blob());

        if (mounted) {
          setMicrosoftPhoto(photoObjectUrl);
        }
      } catch {
        if (mounted) {
          setProfile(null);
          setMicrosoftPhoto(undefined);
        }
      }
    };

    loadProfile();

    return () => {
      mounted = false;
      if (photoObjectUrl) {
        URL.revokeObjectURL(photoObjectUrl);
      }
    };
  }, [identityApi, microsoftAuthApi]);

  const avatarSrc = profileAvatar(profile) || microsoftPhoto;
  const label = profile?.displayName || profile?.email || 'Signed in';

  return (
    <TdsHeaderItem slot="end">
      <Link
        to={appRoutePaths.settings}
        aria-label={`Settings for ${label}`}
        className="tegel-app-header-user-link"
        title={label}
      >
        {avatarSrc ? (
          <img
            src={avatarSrc}
            alt=""
            className="tegel-app-header-avatar"
            height={34}
            width={34}
            decoding="async"
            aria-hidden="true"
          />
        ) : (
          <span className="tegel-app-header-avatar" aria-hidden="true">
            {getInitials(profile)}
          </span>
        )}
        <span className="tegel-app-header-user-label">Settings</span>
      </Link>
    </TdsHeaderItem>
  );
};

export const TegelHeader = () => {
  return (
    <TdsHeader
      className="tegel-app-header"
      aria-label="Driveline application header"
    >
      <TdsHeaderItem slot="end">
        <Link
          to={appRoutePaths.search}
          aria-label="Search"
          className="tegel-app-header-icon-link"
        >
          <TdsIcon name="search" size="20px" svgTitle="Search" />
        </Link>
      </TdsHeaderItem>
      <HeaderUserSettings />
      <TdsHeaderBrandSymbol slot="end">
        <a
          href="https://reflex.scania.com"
          aria-label="Driveline home"
          className="tegel-app-header-brand-link"
        />
      </TdsHeaderBrandSymbol>
    </TdsHeader>
  );
};
